import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ModalDialog } from '@kanos/ui';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ACTIVE_WINDOW_MS, noteActivity } from './activity.js';
import { api, ApiError, failure } from './api.js';
import { sessionEnded } from './session-end.js';
import { currentPath, startSignIn, useSignOut } from './session.js';
/** Warn this long before the idle timeout: time enough to come back to the desk. */
const WARN_IDLE_MS = 2 * 60_000;
/** Warn this long before the absolute end: time enough to save what is open. */
const WARN_ABSOLUTE_MS = 5 * 60_000;
/**
 * How long the session still runs. Asked as often as the activity counts (`ACTIVE_WINDOW_MS`):
 * while the person works, this is the request that holds the session open; while nobody works,
 * it goes in the background and only watches - in a visible tab. A hidden tab does not ask: its
 * clock runs on the last answer, and asks once more before it ends (`sessionEnded`); coming back
 * to the tab asks at once.
 */
const sessionTimeoutsQuery = queryOptions({
    queryKey: ['session-timeouts'],
    queryFn: async () => {
        const { data, error, response } = await api.GET('/api/v1/me/session');
        if (!data)
            throw failure('getSessionTimeouts', error, response.status);
        return data;
    },
    refetchInterval: ACTIVE_WINDOW_MS,
    refetchIntervalInBackground: false,
    // Fresh on coming back to the tab - but not again with every flick of focus.
    staleTime: 10_000,
    retry: false,
});
/** „1:05“ - minutes and seconds left. */
const clock = (ms) => {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`;
};
/** The time now: once when `from` comes, then every second - a clock only while it is shown. */
function useNowFrom(from) {
    const [now, setNow] = useState(Date.now);
    const ticking = from !== null && now >= from;
    useEffect(() => {
        if (from === null)
            return undefined;
        const tick = () => setNow(Date.now());
        if (ticking) {
            const timer = window.setInterval(tick, 1000);
            return () => window.clearInterval(timer);
        }
        const timer = window.setTimeout(tick, Math.max(0, from - Date.now()));
        return () => window.clearTimeout(timer);
    }, [from, ticking]);
    return now;
}
/**
 * Warns before the session ends (ADR-005, WCAG 2.2.1 „Zeitbegrenzungen anpassbar“): two
 * minutes before the idle timeout with the way to stay, five minutes before the absolute end
 * with the advice to save. Once it is over, `onEnded` takes the app off the screen - data of the
 * firm stays on no unattended screen.
 */
export function SessionExpiry({ onEnded }) {
    const timeouts = useQuery(sessionTimeoutsQuery);
    const signOut = useSignOut();
    const [acknowledged, setAcknowledged] = useState(null);
    // Only the press spins the button - the polling in the background does not.
    const [staying, setStaying] = useState(false);
    const received = timeouts.dataUpdatedAt;
    const idleEnd = timeouts.data ? received + timeouts.data.idleSeconds * 1000 : null;
    const absoluteEnd = timeouts.data ? received + timeouts.data.absoluteSeconds * 1000 : null;
    const idleFirst = idleEnd !== null && absoluteEnd !== null && idleEnd <= absoluteEnd;
    const end = idleEnd !== null && absoluteEnd !== null ? Math.min(idleEnd, absoluteEnd) : null;
    const warnFrom = end === null ? null : end - (idleFirst ? WARN_IDLE_MS : WARN_ABSOLUTE_MS);
    const now = useNowFrom(warnFrom);
    const left = end === null ? null : end - now;
    const lost = timeouts.error instanceof ApiError && timeouts.error.code === 'auth.session-required';
    const [checked, setChecked] = useState(null);
    const ranOut = left !== null && left <= 0;
    const { refetch } = timeouts;
    useEffect(() => {
        if (!ranOut || checked === received)
            return;
        // The clock ran out, perhaps in a hidden tab - another tab may have kept the session open.
        void refetch().then((answer) => setChecked(answer.dataUpdatedAt));
    }, [ranOut, checked, received, refetch]);
    const ended = sessionEnded({ lost, left, received, checked });
    useEffect(() => {
        if (ended)
            onEnded();
    }, [ended, onEnded]);
    if (ended || left === null || warnFrom === null || now < warnFrom)
        return null;
    const stay = () => {
        noteActivity();
        setStaying(true);
        void timeouts.refetch().finally(() => setStaying(false));
    };
    if (idleFirst) {
        return (_jsx(ModalDialog, { title: "Sind Sie noch da?", description: "Zum Schutz der Daten Ihrer Mandanten meldet Kanos Sie nach einer Weile ohne Aktivit\u00E4t ab.", isOpen: true, onOpenChange: (open) => {
                if (!open)
                    stay();
            }, children: () => (_jsxs("div", { className: "grid gap-4", children: [_jsxs("p", { className: "text-lg", children: ["Abmeldung in", ' ', _jsx("span", { "aria-hidden": true, className: "font-semibold tabular-nums", children: clock(left) }), _jsxs("span", { className: "sr-only", children: ["weniger als ", Math.ceil(left / 60_000), " Minuten"] })] }), _jsxs("div", { className: "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", children: [_jsx(Button, { variant: "quiet", isPending: signOut.isPending, onPress: () => signOut.mutate(), children: "Jetzt abmelden" }), _jsx(Button, { isPending: staying, onPress: stay, children: "Angemeldet bleiben" })] })] })) }));
    }
    if (acknowledged === end)
        return null;
    return (_jsx(ModalDialog, { title: "Ihre Anmeldung endet gleich", description: "Jede Anmeldung endet nach einer festen Zeit, auch wenn Sie arbeiten. Sichern Sie, was offen ist, und melden Sie sich danach neu an.", isOpen: true, onOpenChange: (open) => {
            if (!open)
                setAcknowledged(end);
        }, children: (close) => (_jsxs("div", { className: "grid gap-4", children: [_jsxs("p", { className: "text-lg", children: ["Ende in", ' ', _jsx("span", { "aria-hidden": true, className: "font-semibold tabular-nums", children: clock(left) }), _jsxs("span", { className: "sr-only", children: ["weniger als ", Math.ceil(left / 60_000), " Minuten"] })] }), _jsxs("div", { className: "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", children: [_jsx(Button, { variant: "quiet", onPress: () => startSignIn(currentPath()), children: "Jetzt neu anmelden" }), _jsx(Button, { onPress: close, children: "Weiterarbeiten" })] })] })) }));
}
//# sourceMappingURL=session-expiry.js.map